import React, { useState, useEffect, useContext, useRef } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import { formatDateRu } from '../utils/formatUtils';
import CRMTradingPanel from './CRMTradingPanel'; // [CFD-2026-05]

// Добавляем interceptor для обработки ошибок JSON
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.message === 'Unexpected end of JSON input' || 
        error.message.includes('JSON')) {
      console.error('JSON Parse Error:', error);
      error.message = 'Сервер вернул некорректные данные. Попробуйте обновить страницу.';
    }
    return Promise.reject(error);
  }
);
import { 
  ArrowLeft,
  DollarSign,
  TrendingUp,
  TrendingDown,
  Play,
  Pause,
  Settings,
  RefreshCw,
  Edit,
  Save,
  X,
  Clock,
  Target,
  AlertCircle,
  CheckCircle,
  XCircle
} from 'lucide-react';
import axios from 'axios';

import { getBackendUrl } from '../utils/api';

const API = `${getBackendUrl()}/api`;

const TradingAccountDetail = () => {
  const { accountId } = useParams();
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  
  const [account, setAccount] = useState(null);
  const { tt } = useLanguage();
  const [activeTrades, setActiveTrades] = useState([]);
  // [CFD-2026-05-14] Widget should reflect total active trades = options + CFDs
  const [cfdOpenCount, setCfdOpenCount] = useState(0);
  const [tradeHistory, setTradeHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [closingTradeId, setClosingTradeId] = useState(null);
  
  // Редактирование настроек
  const [editingSettings, setEditingSettings] = useState(false);
  // [REFRESH-2026-05-19g] Child (CRMTradingPanel) reports when its own edit
  // modals are open so we also pause the 30s refresh while admin is editing.
  const [panelModalOpen, setPanelModalOpen] = useState(false);
  const [settings, setSettings] = useState({
    trading_enabled: true,
    profit_enabled: true,
    force_result_mode: 'auto', // 'win', 'loss', 'auto'
    max_trade_amount: 1000,
    payout_percentage: 85,
    balance: 0
  });
  
  // Модальные окна
  const [showBalanceModal, setShowBalanceModal] = useState(false);
  const [newBalance, setNewBalance] = useState('');
  const [balanceAction, setBalanceAction] = useState('set'); // 'set', 'add', 'subtract'

  // [CFD-2026-05-12] Lift CRMTradingPanel tab state — Trading Settings only show on Options tab
  const [crmTab, setCrmTab] = useState('cfd');

  useEffect(() => {
    fetchAccountDetail();
    // [REFRESH-2026-05-19h] Pause-while-editing via a ref so the interval
    // effect doesn't re-run (and immediately re-fetch) every time editing
    // state changes. Previously listing edit flags in deps caused a refetch
    // exactly on click "Изменить" which competed with the modal opening.
    const interval = setInterval(() => {
      if (refreshPausedRef.current) return; // any modal open → skip
      fetchActiveTrades();
      fetchAccountDetail();
    }, 30000);
    return () => clearInterval(interval);
  }, [accountId]);

  // Refresh-pause flag (single ref read by the stable interval above)
  const refreshPausedRef = useRef(false);
  useEffect(() => {
    refreshPausedRef.current = Boolean(editingSettings || panelModalOpen || showBalanceModal);
  }, [editingSettings, panelModalOpen, showBalanceModal]);

  const fetchAccountDetail = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/trading/admin/accounts/${accountId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      // Проверяем что response содержит валидные данные
      if (!response.data || typeof response.data !== 'object') {
        throw new Error('Invalid response data format');
      }

      console.log('✅ Account detail fetched:', response.data);
      
      // Проверяем что account существует
      if (!response.data.account) {
        throw new Error('Account data not found in response');
      }
      
      setAccount(response.data.account);
      setActiveTrades(response.data.activeTrades || []);
      setTradeHistory(response.data.tradeHistory || []);

      // [CFD-2026-05-14] Fetch CFD open positions for the widget count
      try {
        const cid = response.data.account?.client_id;
        if (cid !== undefined && cid !== null) {
          const cfdResp = await axios.get(
            `${API}/cfd/admin/positions?client_id=${cid}&status=open`,
            { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } }
          );
          setCfdOpenCount(Array.isArray(cfdResp.data) ? cfdResp.data.length : 0);
        }
      } catch (e) { /* widget count is best-effort */ }
      
      // Обновляем настройки
      setSettings({
        trading_enabled: response.data.account.trading_enabled ?? true,
        profit_enabled: response.data.account.profit_enabled ?? true,
        force_result_mode: response.data.account.force_result_mode ?? 'auto',
        max_trade_amount: response.data.account.max_trade_amount || 1000,
        payout_percentage: response.data.account.payout_percentage || 85,
        balance: response.data.account.balance || 0
      });
    } catch (error) {
      console.error('❌ Error fetching account detail:', error);
      
      // Более детальная обработка ошибок
      if (error.message === 'Invalid response data format') {
        alert(tt('Ошибка: Сервер вернул некорректные данные'));
      } else if (error.response?.status === 404) {
        navigate('/crm/trading-accounts');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchActiveTrades = async () => {
    if (refreshing) return;
    
    try {
      setRefreshing(true);
      const response = await axios.get(`${API}/trading/admin/accounts/${accountId}/active-trades`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      const trades = response.data.activeTrades || [];
      console.log('📊 CRM Active Trades загружены:', {
        count: trades.length,
        trades: trades.map(t => ({
          id: t.id?.substring(0, 12),
          asset: t.asset,
          duration: t.duration,
          created_at: t.created_at,
          expires_at: t.expires_at,
          status: t.status
        }))
      });
      
      setActiveTrades(trades);
    } catch (error) {
      console.error('❌ Error fetching active trades:', error);
    } finally {
      setRefreshing(false);
    }
  };

  const handleSaveSettings = async () => {
    try {
      await axios.put(`${API}/trading/admin/accounts/${accountId}/settings`, settings, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      console.log('✅ Settings saved successfully');
      setEditingSettings(false);
      fetchAccountDetail(); // Обновляем данные
      alert(tt('Настройки сохранены успешно!'));
    } catch (error) {
      console.error('❌ Error saving settings:', error);
      alert(`Ошибка сохранения настроек: ${error.response?.data?.detail || error.message}`);
    }
  };

  const handleBalanceUpdate = async () => {
    try {
      const amount = parseFloat(newBalance);
      if (isNaN(amount) || amount < 0) {
        alert(tt('Введите корректную сумму'));
        return;
      }

      await axios.post(`${API}/trading/admin/accounts/${accountId}/balance`, {
        action: balanceAction,
        amount: amount
      }, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      console.log(`✅ Balance ${balanceAction} completed: ${amount}`);
      setShowBalanceModal(false);
      setNewBalance('');
      fetchAccountDetail(); // Обновляем данные
      alert(tt('Баланс обновлен успешно!'));
    } catch (error) {
      console.error('❌ Error updating balance:', error);
      alert(`Ошибка обновления баланса: ${error.response?.data?.detail || error.message}`);
    }
  };

  const handleCloseTrade = async (tradeId, forceResult = null) => {
    try {
      setClosingTradeId(tradeId); // Показываем индикатор загрузки
      
      const response = await axios.post(`${API}/trading/admin/accounts/${accountId}/close-trade`, {
        trade_id: tradeId,
        force_result: forceResult // 'win', 'loss', null (auto)
      }, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      console.log(`✅ Trade ${tradeId} closed with result: ${forceResult || 'auto'}`);
      
      // Проверяем что response содержит валидные данные
      if (!response.data || typeof response.data !== 'object') {
        console.warn('Invalid response data from close-trade endpoint');
      }
      
      // Показываем результат пользователю
      const resultText = forceResult === 'win' ? 'В ПРИБЫЛЬ' : 
                        forceResult === 'loss' ? 'В УБЫТОК' : 'АВТО';
      const profit = response.data?.profit || 0;
      alert(`Сделка закрыта: ${resultText}\nРезультат: ${profit >= 0 ? '+' : ''}${profit.toFixed(2)}$`);
      
      // Сразу убираем сделку из списка активных
      setActiveTrades(prevTrades => prevTrades.filter(trade => trade.id !== tradeId));
      
      // Обновляем все данные
      fetchAccountDetail();
    } catch (error) {
      console.error('❌ Error closing trade:', error);
      alert(`Ошибка закрытия сделки: ${error.response?.data?.detail || error.message}`);
    } finally {
      setClosingTradeId(null); // Скрываем индикатор загрузки
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const formatTimeLeft = (expiresAt) => {
    // ИСПРАВЛЕНО: Используем Date.now() для получения UTC timestamp
    // Date.now() возвращает миллисекунды с Unix epoch в UTC, не зависит от timezone браузера
    const nowTimestamp = Date.now();
    
    // Parse expiry date - backend always sends UTC with +00:00
    const expiry = new Date(expiresAt);
    const expiryTimestamp = expiry.getTime();
    
    // Calculate time difference in milliseconds
    const timeLeft = expiryTimestamp - nowTimestamp;
    
    const totalSeconds = Math.floor(timeLeft / 1000);
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    
    // РАСШИРЕННАЯ ДИАГНОСТИКА - всегда логируем
    console.log('🕐 formatTimeLeft вызвана:', {
      expiresAt,
      nowTimestamp: new Date(nowTimestamp).toISOString(),
      expiryTimestamp: new Date(expiryTimestamp).toISOString(),
      timeLeft,
      timeLeftSeconds: totalSeconds,
      calculated: {days, hours, minutes, seconds},
      willDisplay: timeLeft <= 0 ? 'Истекла' : (days > 0 ? `${days}д ${hours}ч` : (hours > 0 ? `${hours}ч ${minutes}м` : `${minutes}:${seconds.toString().padStart(2, '0')}`))
    });
    
    if (timeLeft <= 0) return 'Истекла';
    
    // Format based on time left
    if (days > 0) {
      return `${days}д ${hours}ч`;
    } else if (hours > 0) {
      return `${hours}ч ${minutes}м`;
    } else {
      return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    }
  };

  const getTradeResultColor = (status) => {
    switch (status) {
      case 'won': return 'text-green-600';
      case 'lost': return 'text-red-600';
      default: return 'text-gray-600';
    }
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="flex items-center justify-center py-12">
          <div className="spinner mr-3"></div>
          <span className="text-gray-600">{tt('Загрузка торгового аккаунта...')}</span>
        </div>
      </div>
    );
  }

  if (!account) {
    return (
      <div className="page-container">
        <div className="text-center py-12">
          <AlertCircle className="w-12 h-12 mx-auto mb-4 text-red-300" />
          <p className="text-red-600">{tt('Торговый аккаунт не найден')}</p>
          <Link to="/crm/trading-accounts" className="btn btn-primary mt-4">
            {tt('Вернуться к списку')}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      {/* Header */}
      <div className="page-header">
        <div className="flex items-center space-x-4">
          <Link
            to="/crm/trading-accounts"
            className="text-gray-600 hover:text-gray-900 transition-colors"
          >
            <ArrowLeft className="w-6 h-6" />
          </Link>
          <div>
            <h1 className="page-title">
              Торговый аккаунт: {account.client_name}
            </h1>
            <p className="page-subtitle">
              ID: {account.client_id} | Email: {account.client_email}
            </p>
          </div>
        </div>
        
        <div className="flex space-x-2">
          <button
            onClick={fetchAccountDetail}
            className="btn btn-secondary flex items-center space-x-2"
            disabled={refreshing}
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            <span>{tt('Обновить')}</span>
          </button>
          <button
            onClick={() => setShowBalanceModal(true)}
            className="btn btn-primary flex items-center space-x-2"
          >
            <DollarSign className="w-4 h-4" />
            <span>{tt('Изменить баланс')}</span>
          </button>
        </div>
      </div>

      {/* Account Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
        <div className="card p-6">
          <div className="flex items-center">
            <DollarSign className="w-8 h-8 text-green-500 mr-3" />
            <div>
              <div className="text-3xl font-bold text-green-600">{formatCurrency(account.balance)}</div>
              <div className="text-sm text-gray-600">{tt('Текущий баланс')}</div>
            </div>
          </div>
        </div>
        
        <div className="card p-6">
          <div className="flex items-center">
            <TrendingUp className="w-8 h-8 text-blue-500 mr-3" />
            <div>
              <div className="text-3xl font-bold text-blue-600">{formatCurrency(account.total_invested || 0)}</div>
              <div className="text-sm text-gray-600">{tt('Всего инвестировано')}</div>
            </div>
          </div>
        </div>
        
        <div className="card p-6">
          <div className="flex items-center">
            <Target className="w-8 h-8 text-purple-500 mr-3" />
            <div>
              <div className={`text-3xl font-bold ${account.total_profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {formatCurrency(account.total_profit || 0)}
              </div>
              <div className="text-sm text-gray-600">{tt('Общая прибыль')}</div>
            </div>
          </div>
        </div>
        
        <div className="card p-6">
          <div className="flex items-center">
            <Clock className="w-8 h-8 text-orange-500 mr-3" />
            <div>
              <div className="text-3xl font-bold text-orange-600" data-testid="account-active-trades-count">
                {activeTrades.length + cfdOpenCount}
              </div>
              <div className="text-sm text-gray-600">{tt('Активные сделки')}</div>
              {cfdOpenCount > 0 && (
                <div className="text-xs text-gray-500">
                  {activeTrades.length} {tt('опц.')} · {cfdOpenCount} CFD
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* [CFD-2026-05] Управление CFD сделками + Trading Settings (only on Options tab) */}
      {account?.client_id && (() => {
        const tradingSettingsCard = (
          <div className="mb-4 p-4 border border-gray-200 rounded-lg bg-gray-50">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-base font-semibold text-gray-900">{tt('Торговые настройки')}</h3>
              {editingSettings ? (
                <div className="flex space-x-2">
                  <button onClick={handleSaveSettings} className="btn btn-primary flex items-center space-x-2">
                    <Save className="w-4 h-4" /><span>{tt('Сохранить')}</span>
                  </button>
                  <button
                    onClick={() => { setEditingSettings(false); fetchAccountDetail(); }}
                    className="btn btn-secondary flex items-center space-x-2"
                  >
                    <X className="w-4 h-4" /><span>{tt('Отмена')}</span>
                  </button>
                </div>
              ) : (
                <button onClick={() => setEditingSettings(true)} className="btn btn-secondary flex items-center space-x-2">
                  <Edit className="w-4 h-4" /><span>{tt('Редактировать')}</span>
                </button>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">{tt('Торговля разрешена')}</label>
                {editingSettings ? (
                  <select className="input" value={settings.trading_enabled}
                    onChange={(e) => setSettings({ ...settings, trading_enabled: e.target.value === 'true' })}>
                    <option value="true">{tt('Разрешена')}</option>
                    <option value="false">{tt('Заблокирована')}</option>
                  </select>
                ) : (
                  <div className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium ${settings.trading_enabled ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                    {settings.trading_enabled
                      ? (<><Play className="w-4 h-4 mr-1" /> {tt('Разрешена')}</>)
                      : (<><Pause className="w-4 h-4 mr-1" /> {tt('Заблокирована')}</>)}
                  </div>
                )}
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">{tt('Результат сделок')}</label>
                {editingSettings ? (
                  <select className="input" value={settings.force_result_mode}
                    onChange={(e) => setSettings({ ...settings, force_result_mode: e.target.value })}>
                    <option value="win">{tt('Все сделки в прибыль')}</option>
                    <option value="loss">{tt('Все сделки в убыток')}</option>
                    <option value="auto">{tt('Автоматически')}</option>
                  </select>
                ) : (
                  <div className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium ${
                    settings.force_result_mode === 'win' ? 'bg-green-100 text-green-800'
                    : settings.force_result_mode === 'loss' ? 'bg-red-100 text-red-800'
                    : 'bg-blue-100 text-blue-800'}`}>
                    {settings.force_result_mode === 'win'  ? (<><TrendingUp className="w-4 h-4 mr-1" /> {tt('Все в прибыль')}</>)
                    : settings.force_result_mode === 'loss' ? (<><TrendingDown className="w-4 h-4 mr-1" /> {tt('Все в убыток')}</>)
                    : (<><RefreshCw className="w-4 h-4 mr-1" /> {tt('Автоматически')}</>)}
                  </div>
                )}
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">{tt('Максимальная сумма сделки')}</label>
                {editingSettings ? (
                  <input type="text" inputMode="decimal" className="input"
                    value={settings.max_trade_amount}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === '' || /^\d*\.?\d*$/.test(value)) {
                        setSettings({ ...settings, max_trade_amount: parseFloat(value) || 0 });
                      }
                    }} />
                ) : (
                  <div className="text-gray-900 font-medium">{formatCurrency(settings.max_trade_amount)}</div>
                )}
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">{tt('Процент выплат')}</label>
                <div className="text-gray-900 font-medium">{settings.payout_percentage}%</div>
              </div>
            </div>
          </div>
        );
        return (
          <div className="card p-6 mb-6">
            <CRMTradingPanel
              clientId={account.client_id}
              accountId={accountId}
              onTabChange={setCrmTab}
              optionsExtraTop={tradingSettingsCard}
              forceResultMode={settings.force_result_mode}
              onCloseOption={handleCloseTrade}
              onModalStateChange={setPanelModalOpen}
            />
          </div>
        );
      })()}

      {/* [CFD-2026-05-13] Active Trades & Trade History moved inside CRMTradingPanel Options tab */}

      {/* Balance Update Modal */}
      {showBalanceModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {tt('Изменить баланс')}
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {tt('Действие')}
                </label>
                <select
                  className="input"
                  value={balanceAction}
                  onChange={(e) => setBalanceAction(e.target.value)}
                >
                  <option value="set">{tt('Установить баланс')}</option>
                  <option value="add">{tt('Добавить к балансу')}</option>
                  <option value="subtract">{tt('Списать с баланса')}</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {tt('Сумма')}
                </label>
                <input
                  type="text"
                  inputMode="decimal"
                  className="input"
                  value={newBalance}
                  onChange={(e) => {
                    const value = e.target.value;
                    // Разрешаем пустое поле, цифры, точку и минус в начале
                    if (value === '' || value === '-' || /^-?\d*\.?\d*$/.test(value)) {
                      setNewBalance(value);
                    }
                  }}
                  placeholder={tt('Введите сумму')}
                />
              </div>
              
              <div className="text-sm text-gray-600">
                <strong>{tt('Текущий баланс:')}</strong> {formatCurrency(account.balance)}
                {newBalance && (
                  <>
                    <br />
                    <strong>{tt('Новый баланс будет:')}</strong> {
                      balanceAction === 'set' 
                        ? formatCurrency(parseFloat(newBalance) || 0)
                        : balanceAction === 'add'
                        ? formatCurrency((account.balance || 0) + (parseFloat(newBalance) || 0))
                        : formatCurrency((account.balance || 0) - (parseFloat(newBalance) || 0))
                    }
                  </>
                )}
              </div>
            </div>
            
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowBalanceModal(false);
                  setNewBalance('');
                }}
                className="btn btn-secondary"
              >
                {tt('Отмена')}
              </button>
              <button
                onClick={handleBalanceUpdate}
                className="btn btn-primary"
                disabled={!newBalance || parseFloat(newBalance) <= 0}
              >
                {tt('Применить')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* [CFD-2026-05] CFD management was moved above. Block removed from bottom. */}
    </div>
  );
};

export default TradingAccountDetail;